import ResizeIcon from '@/widgets/Comment/components/ResizeIcon';
import React, { ReactNode, useEffect, useRef, useState } from 'react';

interface ResizableContainerProps {
  minHeight?: number;
  maxHeight?: number;
  defaultHeight?: number;
  className?: string;
  children: ReactNode;
  allowDrag?: boolean;
}

const ResizableContainer: React.FC<ResizableContainerProps> = ({
  minHeight = 100,
  maxHeight = 800,
  defaultHeight = 300,
  className,
  children,
  allowDrag = true
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const handleRef = useRef<SVGSVGElement | null>(null);
  const [height, setHeight] = useState(defaultHeight);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) return;

    const onMouseDown = (e: MouseEvent | TouchEvent) => {
      draggingRef.current = true;
      startYRef.current = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      startHeightRef.current = wrapperRef.current!.getBoundingClientRect().height;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'ns-resize';
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('touchmove', onMouseMove, { passive: false });
      window.addEventListener('mouseup', onMouseUp);
      window.addEventListener('touchend', onMouseUp);
    };

    const onMouseMove = (e: MouseEvent | TouchEvent) => {
      if (!draggingRef.current) return;
      e.preventDefault();
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as MouseEvent).clientY;
      const dy = clientY - startYRef.current;

      let newHeight = startHeightRef.current - dy;
      if (newHeight < minHeight) newHeight = minHeight;
      if (newHeight > maxHeight) newHeight = maxHeight;
      setHeight(newHeight);
    };

    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchend', onMouseUp);
    };

    handle.addEventListener('mousedown', onMouseDown as any);
    handle.addEventListener('touchstart', onMouseDown as any, { passive: false });

    return () => {
      handle.removeEventListener('mousedown', onMouseDown as any);
      handle.removeEventListener('touchstart', onMouseDown as any);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchmove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchend', onMouseUp);
    };
  }, [minHeight, maxHeight]);

  return (
    <div
      ref={wrapperRef}
      className={`flex flex-col border border-gray-300 rounded ${className || ''}`}
      style={{
        height,
        minHeight,
        maxHeight
      }}
    >
      <ResizeIcon
        ref={handleRef}
        className={`h-9 cursor-ns-resize absolute z-[100] top-[4px] left-1/2 -translate-x-1/2 rounded-[10px] hover:text-[gray] transition-colors ${!allowDrag && 'hidden'}`}
      />
      <div className='flex-1 min-h-0'>{children}</div>
    </div>
  );
};

export default ResizableContainer;
